import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  EmbeddedViewRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { ADYEN_ERROR_CONSTANTS } from '../../../constants/adyen-error.constants';
import { ADYEN_HOSTED_CONSTANTS } from '../../../constants/adyen-hosted.constants';
import { ADYEN_RESUME_CONSTANTS } from '../../../constants/adyen-resume.constants';
import { ADYEN_RESULT_CODE } from '../../../constants/adyen-result-code.constants';
import { WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import {
  AdyenHostedResultResponse,
  AdyenPaymentDetails,
  CreateAdyenSessionResponse,
} from '../../../models/adyen.model';
import { AdyenResumeKind, AdyenResumeSnapshot } from '../../../models/adyen-resume.model';
import { CartItem, EventCartItem } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { buildAdyenBookingContext, buildAdyenHoldScope } from '../../../services/adyen-booking-context.util';
import { adyenApiOrigin, isAdyenHostedResultMessage } from '../../../services/adyen-hosted.util';
import { AdyenLoaderService } from '../../../services/adyen-loader.service';
import { AdyenPaymentHoldService } from '../../../services/adyen-payment-hold.service';
import { AdyenResumeService } from '../../../services/adyen-resume.service';
import { nextAdyenPollDelay, stripAdyenReturnParams } from '../../../services/adyen-resume.util';
import { AppointmentService } from '../../../services/appointment.service';
import { adyenErrorMessageKey } from '../../../services/booking-outcome.util';
import { CustomEventService } from '../../../services/custom-event.service';
import { LoggerService } from '../../../services/logger.service';
import { PaymentService } from '../../../services/payment.service';

/**
 * Adyen Drop-in host.
 *
 * Uses the **Sessions flow**: the backend mints a session, the SDK runs the
 * entire checkout — including 3DS and redirect methods like TWINT — and reports
 * back. There is no `/payments/details` plumbing here because the Sessions flow
 * does not need any; the earlier assumption that it would was wrong.
 *
 * Two things this component deliberately does NOT do:
 *
 *  - it never reports an amount or a status to `/book`. It forwards the session
 *    id and the opaque `sessionResult`, and the backend reads the truth from
 *    Adyen. A component that could assert "this was paid" would be the whole
 *    attack surface.
 *  - it never mounts twice against the same container. Drop-in owns its DOM,
 *    and a second mount produces two payment forms where the shopper can pay
 *    on the stale one.
 */
@Component({
  selector: 'app-cw-adyen',
  templateUrl: './cw-adyen.component.html',
  styleUrls: ['./cw-adyen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, ButtonComponent],
  // `<slot>` is not an Angular element; same reason StripeComponent declares it.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CwAdyenComponent implements OnInit, AfterViewInit, OnDestroy {

  private static slotSequence = 0;
  /** Shared with StripeComponent on purpose: one hardening style covers both. */
  private static readonly SLOT_CLASS = 'calenso-payment-slot';
  private static readonly HARDENING_STYLE_ID = 'calenso-payment-slot-hardening';

  readonly partner = input<Partner>();
  readonly widgetType = input<string>();
  readonly appointmentServicesAvailable = input<any>();
  readonly storeId = input<number>();
  readonly bookingLinkUuid = input<string>();
  readonly predefinedSlotUuid = input<string>();
  /** `/book`'s own combi flag - shared, never re-derived (see adyen-booking-context.util). */
  readonly isCombiServiceSelected = input<boolean | undefined>(undefined);
  readonly shopperEmail = input<string>();
  readonly lang = input<string>();
  /**
   * True only where the HOST can drive `submit()` from its own Book button.
   *
   * Drop-in's pay button is suppressed ONLY there. It has to be an input rather than a constant:
   * this component is rendered by four surfaces (appointment, event, compact-widget,
   * pre-defined-slots) and only the appointment one wires Book to `submitAdyen()`. Hard-coding
   * `showPayButton: false` therefore left the other three with no pay button AND nothing to
   * submit the form - Adyen rendered and could never be paid, and their Book button could never
   * validate, because each of them gates on `adyenPaymentDetails` being set first.
   */
  readonly hostDrivesSubmit = input<boolean>(false);
  /**
   * The host's booking as it stands now, for the resume record written before the shopper leaves
   * the page mid-payment (a redirect, or the hosted page in this tab). Called synchronously right
   * before the navigation, so it describes exactly the booking being paid for. Without it the page
   * cannot be resumed, and a payment is never sent away from a page that cannot come back to it.
   */
  readonly resumeSnapshot = input<(() => AdyenResumeSnapshot | null) | undefined>(undefined);

  /*
   * Seeded by the parent, but the widget's own event bus can override them
   * mid-flow: changing the cart or the guest count changes the amount, and the
   * amount is baked into the Adyen session. `linkedSignal` is the codified
   * shape for that (angular-signals skill, Rule 8 — "derived default + user can
   * override"): a local `.set()` wins until the parent's value changes, which
   * is the same contract the decorator fields had, only explicit.
   */
  readonly priceInput = input<number>(undefined, { alias: 'price' });
  protected readonly price = linkedSignal(() => this.priceInput());

  readonly cartInput = input<CartItem[]>(undefined, { alias: 'cart' });
  protected readonly cart = linkedSignal(() => this.cartInput());

  readonly eventCartInput = input<EventCartItem[]>(undefined, { alias: 'eventCart' });
  protected readonly eventCart = linkedSignal(() => this.eventCartInput());

  /**
   * Fired once Adyen has authorised — carries only opaque handles. The same single signal for every
   * way a payment can complete: Drop-in submitted by the host's Book button, Drop-in submitted by
   * itself (its own pay button, or Enter in a card field), and Adyen's hosted page. The host decides
   * from its own state whether a Book press is waiting for it or whether to book on its own.
   */
  readonly adyenSuccessfulEvent = output<AdyenPaymentDetails>();
  /** Fired on refusal/cancel/error so the parent can re-enable the flow. */
  readonly resetAdyenEvent = output<string | void>();
  /**
   * Fired when Drop-in really starts a payment (its `beforeSubmit`), never merely when the host
   * asked it to. The host uses it to freeze the attempt; an incomplete card never gets here, so
   * the freeze cannot strand a Book button that has nothing coming to release it.
   */
  readonly adyenPaymentStartedEvent = output<void>();

  @ViewChild('dropinTpl', { static: true }) private dropinTpl!: TemplateRef<unknown>;

  /** Unique per instance so two payment surfaces can never project into the same slot. */
  protected readonly slotName = `calenso-payment-${CwAdyenComponent.slotSequence++}`;

  /** The projected container Drop-in mounts into, taken from the created view. */
  private dropinHost?: HTMLElement;
  private projectedView?: EmbeddedViewRef<unknown>;
  private slotHost?: HTMLElement;

  private adyenLoaderService = inject(AdyenLoaderService);
  private adyenPaymentHoldService = inject(AdyenPaymentHoldService);
  private adyenResumeService = inject(AdyenResumeService);
  private appRef = inject(ApplicationRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private appointmentService = inject(AppointmentService);
  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  private paymentService = inject(PaymentService);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  /**
   * A Drop-in is mounted and waiting for the customer: the only state in which "complete the
   * payment before you book" is true. Read by the host to decide whether to say it at all - with
   * no Drop-in (loading, an error, a result) this component already says what is going on.
   */
  private readonly dropinMountedState = signal(false);
  readonly dropinMounted = this.dropinMountedState.asReadonly();

  private dropin: { unmount?: () => void; remove?: () => void } | null = null;
  private mounting = false;
  /**
   * Set in ngOnDestroy. Only the session request is tied to the destroy ref; once
   * mountDropin() is past its first await nothing stops it, so switching the payment
   * method away mid-load would mount a Drop-in into a detached node on a dead instance
   * and - worse - park ITS session over the one a replacement instance just wrote, so a
   * later redirect return would resume the wrong payment.
   */
  private destroyed = false;
  /**
   * Bumped by every teardown. A session request or a mountDropin() that started under an
   * older value belongs to a checkout that has since been replaced - a changed amount, a
   * retry - and must neither mount nor touch the loading state of its successor.
   */
  private generation = 0;
  private sessionRequest?: Subscription;

  /**
   * Errors after which paying again on a NEW session is safe. `pending` is deliberately
   * absent: that money may still arrive, and a second attempt could charge twice.
   */
  private static readonly RETRYABLE_ERRORS: readonly string[] = [
    'adyen.errors.session_failed',
    // `payment_unavailable` answered 503: Adyen could not be reached, which heals on its own.
    ADYEN_ERROR_CONSTANTS.MESSAGE_KEYS.TEMPORARILY_UNAVAILABLE,
    ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.REFUSED,
    'adyen.errors.cancelled',
    ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED,
    // A hosted session outlived its 25 minutes: a new one is the way on.
    ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.EXPIRED,
    // The booking this tab already paid for is made. Pressing it is the customer booking again.
    ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.ALREADY_BOOKED,
  ];
  readonly canRetry = computed(() => CwAdyenComponent.RETRYABLE_ERRORS.includes(this.errorMessage() ?? ''));

  /**
   * The Pending message's own way out. Pending is kept out of RETRYABLE_ERRORS so that nothing
   * starts a new session behind it on its own - but with Adyen as the only method the customer had
   * no way forward except a refresh. Pressing this is their explicit decision to pay again.
   */
  protected readonly isHeld = computed(() => this.errorMessage() === ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING);

  /** Results that mean Adyen took the money, or may still take it. */
  private static readonly MONEY_RESULT_CODES: readonly string[] = [
    ADYEN_RESULT_CODE.AUTHORISED,
    ADYEN_RESULT_CODE.PENDING,
    ADYEN_RESULT_CODE.RECEIVED,
  ];

  /** The amount the currently-mounted session was minted for. */
  private mountedAmount: number | null = null;

  /**
   * A payment has begun here (Drop-in's own `beforeSubmit`) and has not yet produced a result.
   *
   * The host latches its Book button while a payment is in flight, and only a result unlatches
   * it. So if this component dies mid-authorisation - the personal form goes invalid, the total
   * drops to zero, the customer switches payment method - nothing would ever release that latch
   * and Book would silently do nothing for the rest of the session.
   *
   * ngOnDestroy releases it, but ONLY while this is true. On a settled payment the host has
   * already cleared the latch itself, and a spurious reset there would wipe the
   * adyenPaymentDetails that the refund notice needs after a failed /book.
   */
  private paymentInFlight = false;

  /**
   * A session the backend answered for Adyen's HOSTED page: this page's origin is not allowed on
   * the client key, so Drop-in could not mount here. The shopper pays in a popup instead, and the
   * result comes back through the Calenso return page (a message) or the stored result (a poll).
   */
  protected readonly hostedSession = signal<CreateAdyenSessionResponse | null>(null);
  /** The hosted checkout's popup is open and no result has arrived yet. */
  protected readonly hostedAwaiting = signal(false);
  /** The browser refused to open the popup; the shopper has to allow it and press again. */
  protected readonly popupBlocked = signal(false);
  /** The popup's window name, so the "Complete payment" link re-opens the same window. */
  protected readonly popupName = ADYEN_HOSTED_CONSTANTS.POPUP_NAME;

  private hostedPopup: Window | null = null;
  private hostedMessageListener?: (event: MessageEvent) => void;
  /** Re-reads the stored result at once when the shopper comes back to this tab. */
  private hostedReturnListener?: () => void;
  /** The fast read while the popup is open: 2 s, backing off to 10 s. */
  private hostedPollTimeout?: ReturnType<typeof setTimeout>;
  private hostedPollDelay?: number;
  /** The slow read once the popup read as closed. */
  private hostedPollTimer?: ReturnType<typeof setInterval>;
  private hostedClosedTimer?: ReturnType<typeof setInterval>;
  private hostedPollRequest?: Subscription;
  private hostedExpiresAt = 0;
  /** Set by the first result; message and poll race, and only one of them may book. */
  private hostedSettled = false;
  /**
   * The last read of the stored result that "Cancel payment" makes before it abandons a hosted
   * payment. While it runs a second press is ignored, and the timers that could replace it are off.
   */
  private hostedCancelCheck?: Subscription;
  /**
   * The generation of a Drop-in payment the customer cancelled while Adyen was authorising it. Adyen
   * may still have authorised it: such a late result is settled instead of dropped, until a new
   * checkout starts here (from then on the reconciliation job reverses it, as for any stale result).
   */
  private cancelledDropinGeneration?: number;

  /**
   * The browser would not store the resume record, so a Drop-in redirect could never come back to
   * this booking. From then on this instance takes the hosted page, which reports back to this very
   * window instead of reloading it.
   */
  private forceHosted = false;
  /** Shown while the hosted page stands in for a Drop-in that could not have been resumed. */
  protected readonly resumeUnavailable = signal(false);

  /** The initial checkout was deferred until an app-level resume of this partner's payment ends. */
  private waitingForResume = false;

  /**
   * The session this instance last parked a resume record for. A checkout torn down here (replaced,
   * abandoned, destroyed) can never be resumed, so its record goes with it - left behind, a reload
   * up to 25 minutes later would restore that stale booking onto the payment step.
   */
  private parkedSessionId?: string;

  /**
   * Re-mint whenever the amount changes through the INPUT.
   *
   * The two CustomEventService subscriptions cover the cart and guest-count
   * paths, but the parent also recalculates `price` directly - a coupon, a
   * changed service, a removed addon. The amount is baked into the Adyen
   * session, so without this the mounted Drop-in keeps charging the pre-change
   * total and the customer is billed the wrong amount.
   *
   * Guarded on mountedAmount so it cannot fire before the first mount, and
   * startCheckout() does not write any signal read here, so there is no loop.
   *
   * The first effect starts the checkout that ngOnInit deferred while an app-level resume was
   * finishing this partner's payment. Only the resume state is tracked: the checkout it starts reads
   * the cart and the price, and must not make this effect re-run on them.
   */
  constructor() {
    effect(() => {
      if (!this.adyenResumeService.resuming() && this.waitingForResume) {
        this.waitingForResume = false;
        untracked(() => {
          this.isLoading.set(false);
          this.beginCheckout();
        });
      }
    });
    effect(() => {
      const price = this.price() ?? null;

      if (this.mountedAmount !== null && price !== this.mountedAmount) {
        this.teardown();
        this.startCheckout();
      }
    });
  }

  ngOnInit(): void {
    // A payment that left the page (a 3DS redirect, the hosted page in this tab) is being finished
    // at app level (AdyenResumeService). Minting here meanwhile would put a second payable session
    // beside money that is already authorised, so the first checkout waits for it.
    if (this.adyenResumeService.resuming()) {
      this.waitingForResume = true;
      this.isLoading.set(true);
    } else {
      this.beginCheckout();
    }

    // There is deliberately no "re-trigger" subscription here. After a failed /book the
    // parent brings the payment options back (resetPaymentSuccessStateSubject); that
    // re-renders this component, and the startCheckout() above mints the fresh session.
    // A signal sent to THIS instance could never arrive: a successful payment hides the
    // payment options, which destroys it before /book is even sent.

    this.customEventService.cartItemsChangeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.type === WIDGET_CONSTANTS.APPOINTMENT) {
          this.cart.set(result.cart);
        } else {
          this.eventCart.set(result.eventCart);
        }
        if (this.holdsUnsettledPayment() || this.waitingForResume) {
          return;
        }
        // The amount is baked into the Adyen session, so a changed cart makes
        // the mounted Drop-in charge the wrong total. Re-mint straight away:
        // tearing down and waiting for a re-trigger left the customer looking at
        // an empty payment pane, because nothing re-triggered the mount.
        this.teardown();
        this.startCheckout();
      });

    this.customEventService.additionalGuestChangeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (this.price() !== result.finalPrice) {
          this.price.set(result.finalPrice);
          if (this.holdsUnsettledPayment() || this.waitingForResume) {
            return;
          }
          this.teardown();
          this.startCheckout();
        }
      });
  }

  /**
   * The first checkout of this instance.
   *
   * Mounted as soon as this component exists: the parent only renders it when the customer has
   * selected Adyen AND the form is valid, so its presence in the DOM already IS the "start
   * checkout" signal - unlike Saferpay, which opens a popup and therefore has to wait for an
   * explicit click.
   *
   * Unless an earlier instance ended on a result whose money may still arrive: the form going
   * invalid and valid again rebuilds this component, and minting here would put a second payable
   * session beside a payment that can still complete. That instance's message is shown instead.
   */
  private beginCheckout(): void {
    const heldReason = this.adyenPaymentHoldService.heldReason(this.partner()?.id, this.holdScope());
    if (heldReason) {
      this.errorMessage.set(heldReason);
    } else if (this.alreadyBookedHere()) {
      this.errorMessage.set(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.ALREADY_BOOKED);
    } else {
      this.startCheckout();
    }
  }

  /**
   * This tab already sent `/book` with a payment for this very booking (a resumed payment whose
   * answer never arrived: the tab was closed or reloaded). The backend claims a session once, so a
   * new payment here would only charge the customer a second time for a booking that exists.
   */
  private alreadyBookedHere(): boolean {
    const record = this.adyenResumeService.read(this.partner()?.id);
    if (!record?.bookingSent) {
      return false;
    }
    if (record.holdScope === this.holdScope()) {
      return true;
    }
    // A different booking: that one is done with, and this one pays normally.
    this.adyenResumeService.clear(this.partner()?.id);
    return false;
  }

  ngAfterViewInit(): void {
    this.projectDropinIntoLightDom();
  }

  ngOnDestroy(): void {
    // A payment that started here and never reported back would otherwise leave the host's
    // "already paying" latch on for ever - a Book button that silently does nothing. Emitted
    // BEFORE `destroyed` is set, and only in the unsettled window; Angular runs ngOnDestroy
    // before it tears the output down, so the host still receives this.
    if (this.paymentInFlight) {
      this.paymentInFlight = false;
      this.resetAdyenEvent.emit();
    }
    this.destroyed = true;
    this.teardown();
    if (this.projectedView) {
      this.appRef.detachView(this.projectedView);
      this.projectedView.destroy();
      this.projectedView = undefined;
    }
    this.slotHost?.remove();
    this.slotHost = undefined;
    this.dropinHost = undefined;
  }

  /**
   * Move the Drop-in container out of the shadow root into a slotted light-DOM
   * host, so the SDK stylesheet sitting in document.head can reach it.
   */
  private projectDropinIntoLightDom(): void {
    const view = this.dropinTpl.createEmbeddedView({});
    this.appRef.attachView(view);
    this.projectedView = view;

    const target = this.resolveProjectionTarget(this.elementRef.nativeElement.getRootNode());
    view.rootNodes.forEach((node: Node) => target.appendChild(node));
    this.dropinHost = view.rootNodes.find(
      (node: Node): node is HTMLElement =>
        node instanceof HTMLElement && node.classList.contains('cw-adyen__dropin'),
    );
    // In the light DOM Drop-in would inherit the host page's font (the slot host is reset to
    // `initial` from the shadow root), so give its container the widget's own font.
    if (this.dropinHost) {
      this.dropinHost.style.fontFamily = getComputedStyle(this.elementRef.nativeElement).fontFamily;
    }
  }

  /**
   * The slotted host inside the custom element when the widget runs in a shadow
   * root, otherwise the component's own element - there is no boundary to cross.
   */
  private resolveProjectionTarget(root: Node | ShadowRoot): HTMLElement {
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return this.elementRef.nativeElement;
    }
    CwAdyenComponent.hardenSlotAgainstHostPageCss();
    const slotHost = document.createElement('div');
    slotHost.setAttribute('slot', this.slotName);
    slotHost.className = CwAdyenComponent.SLOT_CLASS;
    (root.host as HTMLElement).appendChild(slotHost);
    this.slotHost = slotHost;
    return slotHost;
  }

  /** Identical to StripeComponent's and idempotent by id, so only one is installed. */
  private static hardenSlotAgainstHostPageCss(): void {
    if (document.getElementById(CwAdyenComponent.HARDENING_STYLE_ID)) {
      return;
    }
    const style = document.createElement('style');
    style.id = CwAdyenComponent.HARDENING_STYLE_ID;
    style.textContent = `
      .${CwAdyenComponent.SLOT_CLASS}.${CwAdyenComponent.SLOT_CLASS},
      .${CwAdyenComponent.SLOT_CLASS}.${CwAdyenComponent.SLOT_CLASS} * {
        opacity: 1 !important;
        visibility: inherit !important;
        filter: none !important;
        transform: none !important;
        pointer-events: auto !important;
        max-height: none !important;
        clip-path: none !important;
      }`;
    document.head.appendChild(style);
  }

  /**
   * Mint a session and mount Drop-in.
   */
  private startCheckout(): void {
    if (this.mounting || this.dropin || this.hostedSession()) {
      return;
    }
    if (!this.price() || this.price() <= 0) {
      // A zero-amount booking must never reach a PSP — Adyen rejects it and the
      // customer sees a failed checkout for something that is free. Emit the
      // translation KEY, not English prose: the parent feeds this straight into
      // a message, and every other path here emits a key.
      this.resetAdyenEvent.emit('adyen.errors.nothing_to_pay');
      return;
    }

    this.mounting = true;
    // A new payable session is on its way: a late result of a cancelled payment is no longer settled.
    this.cancelledDropinGeneration = undefined;
    const generation = this.generation;
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.mountedAmount = this.price() ?? null;

    this.sessionRequest = this.paymentService.createAdyenSession({
      // The same builder feeds cw-payment-methods' availability check, so the session is minted
      // for exactly the booking Adyen was offered for.
      ...buildAdyenBookingContext({
        widgetType: this.widgetType(),
        cart: this.cart(),
        eventCart: this.eventCart(),
        storeId: this.storeId(),
        bookingLinkUuid: this.bookingLinkUuid(),
        predefinedSlotUuid: this.predefinedSlotUuid(),
        isCombiServiceSelected: this.isCombiServiceSelected(),
      }),
      amount: this.price(),
      description: this.buildDescription(),
      // Never the raw href: a payment return left on it (redirectResult, adyen_hosted) would be
      // carried into this session's return and replayed.
      return_url: stripAdyenReturnParams(window.location.href),
      return_to: stripAdyenReturnParams(window.location.href),
      country_code: this.partner()?.countryCode,
      lang: this.lang(),
      shopper_email: this.shopperEmail(),
      metadata: {
        widget_type: this.widgetType(),
        partner_id: this.partner()?.id,
        booking_name: this.partner()?.bookingName,
      },
      // Inside a frame, Drop-in's redirect (an issuer-forced 3DS page) would load in the frame, where
      // most issuer pages refuse to render and the payment is stranded: pay on the hosted page.
      ...(this.forceHosted || !this.isTopLevelWindow()
        ? { checkout_mode: ADYEN_HOSTED_CONSTANTS.CHECKOUT_MODE_HOSTED }
        : {}),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => session.mode === ADYEN_HOSTED_CONSTANTS.MODE_HOSTED
          ? this.prepareHostedCheckout(session, generation)
          : this.mountDropin(session, generation),
        error: (error: HttpErrorResponse) => {
          this.mounting = false;
          this.isLoading.set(false);
          LoggerService.error(error);
          // A 4xx is the backend's answer about THIS booking (e.g. its store has no Adyen
          // account) - asking again gets the same answer, so no retry is offered for it.
          // 429 is the exception: the throttle clears on its own, so it stays retryable.
          const isFinal = error?.status >= 400 && error?.status < 500 && error?.status !== 429;
          // A keyed answer says WHY, in the customer's language; an unknown or missing key keeps
          // the status-based text. The retry offer follows the text (RETRYABLE_ERRORS).
          this.fail(
            adyenErrorMessageKey(error)
              ?? (isFinal ? ADYEN_ERROR_CONSTANTS.MESSAGE_KEYS.UNAVAILABLE : 'adyen.errors.session_failed')
          );
        },
      });
  }

  private async mountDropin(session: CreateAdyenSessionResponse, generation: number): Promise<void> {
    try {
      await this.adyenLoaderService.load(session.environment);
      const sdk = this.adyenLoaderService.getSdk();
      if (!sdk) {
        throw new Error('Adyen SDK global not present after load');
      }

      if (this.destroyed || generation !== this.generation) {
        return;
      }

      const checkout = await sdk.AdyenCheckout({
        environment: session.environment,
        clientKey: session.client_key,
        session: {
          id: session.session_id,
          sessionData: session.session_data,
        },
        locale: this.toAdyenLocale(this.lang()),
        analytics: { enabled: false },
        /*
         * Fires only when Drop-in actually submits - i.e. the card is complete. That is the
         * ONLY trustworthy "a payment has started" signal: `submit()` returns true whenever
         * Drop-in does not throw, and on an incomplete card Drop-in throws nothing and simply
         * marks its own fields, so a caller latching on submit()'s return value latches on an
         * attempt that never happened and never gets a callback to clear it.
         *
         * It MUST resolve(data) or the payment never starts. Never add `onSubmit` beside it:
         * defining that switches the SDK to the advanced flow, and this integration has no
         * /payments call of its own.
         */
        beforeSubmit: (
          data: unknown,
          _component: unknown,
          actions: { resolve: (data: unknown) => void; reject: () => void },
        ) => {
          if (this.destroyed || generation !== this.generation) {
            // A dead or replaced checkout must neither start a payment nor freeze its successor's
            // form - the same staleness test the three result callbacks below apply.
            actions.reject();

            return;
          }
          this.paymentInFlight = true;
          this.adyenPaymentStartedEvent.emit();
          // Parked now, while the booking form is known valid: an issuer can answer this payment
          // with a redirect, and the page may be gone before anything else runs. A store that
          // fails here is retried - and acted on - in beforeRedirect.
          this.writeResumeRecord('dropin', session);
          actions.resolve(data);
        },
        /*
         * The issuer forced a full-page redirect (3DS) or a redirect method was chosen. The page is
         * about to be left; it can only be finished on return if the resume record is stored. When
         * the browser refuses to store it, the redirect is refused too - that attempt then fails at
         * Adyen and moves no money - and the shopper pays on the hosted page instead, which reports
         * back to this window rather than reloading it.
         */
        beforeRedirect: (resolve: () => void, reject: () => void) => {
          if (this.destroyed || generation !== this.generation) {
            reject();
            return;
          }
          if (this.writeResumeRecord('dropin', session)) {
            resolve();
            return;
          }
          reject();
          this.fallBackToHosted();
        },
        onPaymentCompleted: (result: { resultCode?: string; sessionResult?: string }) =>
          this.onCompleted(session, result, generation),
        onPaymentFailed: (result: { resultCode?: string }) => this.onFailed(session, result, generation),
        onError: (error: unknown) => {
          // Still reported - but a checkout the customer has already left must not act on it.
          // Of the SDK's callbacks this is the one that needs no Pay press, so it is the one
          // most likely to arrive late: it would wipe the session a REPLACEMENT instance has
          // parked and paint an error over its live Drop-in.
          LoggerService.error(error);
          if (this.destroyed || generation !== this.generation) {
            return;
          }
          this.clearResumeRecord(session.session_id);
          this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
        },
      });

      if (this.destroyed || generation !== this.generation) {
        return;
      }

      if (!this.dropinHost) {
        // Either the customer navigated away while the SDK was loading, or the
        // projection never ran. Failing loudly beats a permanent spinner.
        this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
        return;
      }

      // Adyen answers the session's /setup with the methods this merchant account can
      // actually offer for THIS amount, currency and country. When that list comes back
      // empty the Drop-in still mounts - as an empty, zero-height container - so the
      // customer sees "Credit or debit card" selected, then nothing at all, and a Book
      // button that cannot work. Nothing throws and no callback fires, which is why this
      // has to be checked rather than waited for. Observed on DEV: a CH merchant account
      // asked for USD returned `paymentMethods: {}` and the box rendered 0px high.
      if (this.offeredPaymentMethodCount(checkout) === 0) {
        this.fail(ADYEN_ERROR_CONSTANTS.MESSAGE_KEYS.UNAVAILABLE);
        return;
      }

      this.dropin = new sdk.Dropin(checkout, {
        showStoredPaymentMethods: false,
        // Suppress Drop-in's own pay button ONLY where the host drives submit() from its Book
        // button. There it is the right thing: two buttons let the shopper be charged before the
        // booking form is valid, and leave an authorised payment waiting for a separate press.
        //
        // Everywhere else it MUST stay, or the form cannot be paid at all - see hostDrivesSubmit.
        showPayButton: !this.hostDrivesSubmit(),
      }).mount(this.dropinHost);
      this.dropinMountedState.set(true);
    } catch (error) {
      LoggerService.error(error);
      if (generation !== this.generation) {
        return;
      }
      this.clearResumeRecord(session.session_id);
      this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
    } finally {
      // A replaced checkout must not clear the flags its successor just set.
      if (generation === this.generation) {
        this.mounting = false;
        this.isLoading.set(false);
      }
    }
  }

  /**
   * The backend answered with a session for Adyen's hosted page. Nothing is loaded from Adyen here:
   * the SDK would refuse this origin anyway. The "Pay with card" button takes Drop-in's place.
   */
  private prepareHostedCheckout(session: CreateAdyenSessionResponse, generation: number): void {
    if (this.destroyed || generation !== this.generation) {
      return;
    }
    this.mounting = false;
    this.isLoading.set(false);

    if (!session.url || !session.hosted_nonce || !session.hosted_token) {
      // Without the page there is nothing to open, and without the nonce and its token no result
      // could be read.
      this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
      return;
    }

    // Relative to this device's clock only, never compared with the server's absolute expires_at.
    this.hostedExpiresAt = Date.now()
      + ADYEN_HOSTED_CONSTANTS.SESSION_LIFETIME_MS
      - ADYEN_HOSTED_CONSTANTS.SESSION_EXPIRY_MARGIN_MS;
    this.hostedSettled = false;
    this.popupBlocked.set(false);
    this.hostedSession.set(session);
    // Waiting for the customer, exactly like a mounted Drop-in: the host's "complete the payment
    // before you book" is true now.
    this.dropinMountedState.set(true);
  }

  /**
   * Open the hosted checkout. Must run synchronously inside the click that asked for it - a popup
   * opened from anywhere else is blocked by every browser - so nothing here awaits: the session was
   * minted before the button was offered.
   *
   * A blocked popup does not end the payment. On a top-level page the hosted page opens in this tab
   * instead, after the booking is parked for the return; inside a frame that would replace the
   * frame's content with a page that cannot come back, so the shopper is asked to allow pop-ups and
   * is given a link, which a click opens reliably.
   *
   * @returns true when the payment went on (a window is open, or this tab is on its way to it)
   */
  protected payWithHostedCheckout(): boolean {
    const session = this.hostedSession();
    if (!session?.url || !session.hosted_nonce || this.destroyed || this.hostedSettled) {
      return false;
    }
    if (this.hostedAwaiting()) {
      // Named window: pressing again brings the open one forward instead of opening a second.
      this.hostedPopup?.focus();
      return true;
    }
    if (Date.now() >= this.hostedExpiresAt) {
      // Adyen would show an expired page. A new session is the way on, which a retry mints.
      this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.EXPIRED);
      return false;
    }

    // Parked before every open, not only when the popup is refused: an in-app browser answers with a
    // handle but opens the page in this very view, and a reload while the popup is open loses this
    // document. Either way the page that comes back finds the payment (AdyenResumeService).
    const parked = this.writeResumeRecord('hosted', session);
    let popup: Window | null = null;
    try {
      popup = window.open(session.url, ADYEN_HOSTED_CONSTANTS.POPUP_NAME, ADYEN_HOSTED_CONSTANTS.POPUP_FEATURES);
    } catch (error) {
      LoggerService.error(error);
    }
    if (!popup) {
      return this.continueHostedInThisTab(session, parked);
    }

    this.popupBlocked.set(false);
    this.startHostedWatch(session, popup);

    return true;
  }

  /**
   * The shopper used the "Complete payment" link. When this window can open the page itself it does
   * (and keeps the handle); otherwise the link's own navigation opens it, and the stored result -
   * read here by polling - settles the payment.
   */
  protected onCompletePaymentLink(event: Event): void {
    const session = this.hostedSession();
    if (!session || this.hostedSettled) {
      event.preventDefault();
      return;
    }
    if (this.hostedAwaiting()) {
      // The window is known; the link re-opens it in place (it is the same named window).
      return;
    }
    if (Date.now() >= this.hostedExpiresAt) {
      // Same as the button: Adyen would show an expired page, and a retry mints a new session.
      event.preventDefault();
      this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.EXPIRED);
      return;
    }
    this.writeResumeRecord('hosted', session);
    let popup: Window | null = null;
    try {
      popup = window.open(session.url, ADYEN_HOSTED_CONSTANTS.POPUP_NAME, ADYEN_HOSTED_CONSTANTS.POPUP_FEATURES);
    } catch (error) {
      LoggerService.error(error);
    }
    if (popup) {
      event.preventDefault();
    }
    this.popupBlocked.set(false);
    this.startHostedWatch(session, popup);
  }

  /**
   * The popup was refused. Same tab when this page can come back to the booking; the link otherwise.
   *
   * The watch starts before leaving: when Back restores this very document from the back/forward
   * cache, its poll picks up the result the hosted page stored. A fresh load finds the parked record
   * instead.
   *
   * @param parked the booking was parked for the return
   */
  private continueHostedInThisTab(session: CreateAdyenSessionResponse, parked: boolean): boolean {
    if (parked && this.isTopLevelWindow() && this.hostedPageCanReturnHere()) {
      this.popupBlocked.set(false);
      this.startHostedWatch(session, null);
      this.leavePageFor(session.url as string);
      return true;
    }
    this.popupBlocked.set(true);
    return false;
  }

  /**
   * The backend keeps this page as the return target only up to a length; beyond it the hosted page
   * could not send the shopper back, so this tab must not be left for it.
   */
  private hostedPageCanReturnHere(): boolean {
    return stripAdyenReturnParams(window.location.href).length <= ADYEN_RESUME_CONSTANTS.RETURN_TO_MAX_LENGTH;
  }

  /** Separate so a test can observe the navigation without leaving the page. */
  protected leavePageFor(url: string): void {
    window.location.assign(url);
  }

  private isTopLevelWindow(): boolean {
    try {
      return window.top === window.self;
    } catch {
      return false;
    }
  }

  /** A payment window is open (or may be, when opened by the link without a handle): watch it. */
  private startHostedWatch(session: CreateAdyenSessionResponse, popup: Window | null): void {
    this.hostedPopup = popup;
    this.hostedAwaiting.set(true);
    // The same latch Drop-in's beforeSubmit sets: the host freezes the amount and the method while
    // the shopper is paying in the popup.
    this.paymentInFlight = true;
    this.adyenPaymentStartedEvent.emit();
    this.watchHostedCheckout(session, popup, this.generation);
  }

  /**
   * Wait for the result: the return page's message and the stored result race, and the first one
   * wins. The poll is the source of truth - it still works when the message cannot arrive (the
   * opener link was cut, the page opened as a new tab on a phone) - and it is repeated at once when
   * the shopper comes back to this tab, because a background tab's timers are throttled.
   */
  private watchHostedCheckout(session: CreateAdyenSessionResponse, popup: Window | null, generation: number): void {
    this.stopHostedWatch();
    const nonce = session.hosted_nonce as string;
    const expectedOrigin = adyenApiOrigin(environment.apiUrl);

    this.hostedMessageListener = (event: MessageEvent) => {
      if (!isAdyenHostedResultMessage(event, nonce, expectedOrigin)) {
        return;
      }
      if (event.data.sessionId !== session.session_id) {
        return;
      }
      this.settleHostedCheckout(session, event.data.sessionResult, generation);
    };
    window.addEventListener('message', this.hostedMessageListener);

    this.hostedReturnListener = () => {
      if (document.visibilityState !== 'hidden') {
        this.pollHostedResult(session, generation, false);
      }
    };
    window.addEventListener('focus', this.hostedReturnListener);
    document.addEventListener('visibilitychange', this.hostedReturnListener);

    this.hostedPollDelay = undefined;
    this.scheduleHostedPoll(session, generation);

    if (popup) {
      this.hostedClosedTimer = setInterval(() => {
        if (popup.closed) {
          this.onHostedPopupClosed(session, generation);
        }
      }, ADYEN_HOSTED_CONSTANTS.CLOSED_CHECK_INTERVAL_MS);
    }
  }

  /** The next fast read: 2 s, then backing off to 10 s. */
  private scheduleHostedPoll(session: CreateAdyenSessionResponse, generation: number): void {
    if (this.hostedPollTimeout) {
      clearTimeout(this.hostedPollTimeout);
    }
    this.hostedPollDelay = nextAdyenPollDelay(this.hostedPollDelay);
    this.hostedPollTimeout = setTimeout(() => {
      this.hostedPollTimeout = undefined;
      if (Date.now() >= this.hostedExpiresAt) {
        // The session is dead, so no result can still come. A retry mints a new one.
        this.closeHostedPopup();
        this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.EXPIRED);
        return;
      }
      this.pollHostedResult(session, generation, false);
      this.scheduleHostedPoll(session, generation);
    }, this.hostedPollDelay);
  }

  /**
   * One read of the stored result, with the nonce and the token only this window was given.
   *
   * @param isFinal the read after the popup closed: it replaces any read still in flight, and a
   *                pending answer (or none at all) then releases the checkout
   */
  private pollHostedResult(session: CreateAdyenSessionResponse, generation: number, isFinal: boolean): void {
    if (this.hostedPollRequest && !this.hostedPollRequest.closed) {
      if (!isFinal) {
        return;
      }
      this.hostedPollRequest.unsubscribe();
    }

    this.hostedPollRequest = this.paymentService
      .getAdyenHostedResult(session.hosted_nonce as string, session.hosted_token as string)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: AdyenHostedResultResponse) => {
          const sessionResult = this.returnedSessionResult(session, result);
          if (sessionResult) {
            this.settleHostedCheckout(session, sessionResult, generation);
          } else if (isFinal) {
            this.releaseHostedCheckout(session, generation);
          }
        },
        error: (error: HttpErrorResponse) => {
          // A missed read is not a failed payment: the next one, or the message, still settles it.
          LoggerService.warn(error);
          if (isFinal) {
            this.releaseHostedCheckout(session, generation);
          }
        },
      });
  }

  /** The stored result's sessionResult when it reports THIS session back from Adyen, else null. */
  private returnedSessionResult(session: CreateAdyenSessionResponse, result: AdyenHostedResultResponse): string | null {
    return result?.status === ADYEN_HOSTED_CONSTANTS.RESULT_STATUS.RETURNED
      && result.session_id === session.session_id
      && !!result.session_result
      ? result.session_result
      : null;
  }

  /** The popup closed. Maybe after paying: one last read decides. */
  private onHostedPopupClosed(session: CreateAdyenSessionResponse, generation: number): void {
    if (this.hostedClosedTimer) {
      clearInterval(this.hostedClosedTimer);
      this.hostedClosedTimer = undefined;
    }
    this.stopFastPoll();
    this.hostedPopup = null;
    this.pollHostedResult(session, generation, true);
  }

  /**
   * The popup closed without a result: the shopper gave up or closed it by accident. Not an error -
   * the button comes back, and the host's in-flight latch comes off so Book works again.
   *
   * "Closed" is not proof the shopper stopped paying: a page in the popup can cut its link to this
   * window, which then reads it as closed while the payment goes on. So the message listener stays
   * and the stored result is still read, slower, until the session expires - a late result settles
   * the payment. A checkout replaced meanwhile (new amount) bumps the generation and stops this
   * watch in teardown(), so a stale result is dropped, never booked.
   */
  private releaseHostedCheckout(session: CreateAdyenSessionResponse, generation: number): void {
    if (this.destroyed || generation !== this.generation || this.hostedSettled) {
      return;
    }
    this.stopFastPoll();
    if (this.hostedPollTimer) {
      clearInterval(this.hostedPollTimer);
    }
    if (this.hostedClosedTimer) {
      clearInterval(this.hostedClosedTimer);
      this.hostedClosedTimer = undefined;
    }
    this.hostedPopup = null;
    this.hostedAwaiting.set(false);
    if (this.paymentInFlight) {
      this.paymentInFlight = false;
      this.resetAdyenEvent.emit();
    }

    this.hostedPollTimer = setInterval(() => {
      if (Date.now() >= this.hostedExpiresAt) {
        // Nothing can arrive for a dead session any more. The button stays; pressing it then
        // reports the expiry and a retry mints a new session.
        this.stopHostedWatch();
        return;
      }
      this.pollHostedResult(session, generation, false);
    }, ADYEN_HOSTED_CONSTANTS.LATE_RESULT_POLL_INTERVAL_MS);
  }

  /**
   * A result for the hosted payment arrived. Handed on exactly as Drop-in's is: the session id and
   * the opaque sessionResult, which `/book` verifies with Adyen. Nothing here decides "paid".
   */
  private settleHostedCheckout(session: CreateAdyenSessionResponse, sessionResult: string, generation: number): void {
    if (this.hostedSettled || this.destroyed || generation !== this.generation) {
      return;
    }
    this.hostedSettled = true;
    this.stopHostedWatch();
    this.closeHostedPopup();
    // Settled in this document, which books it now: a later load must not recover it a second time.
    this.clearResumeRecord(session.session_id);
    this.hostedAwaiting.set(false);
    this.dropinMountedState.set(false);

    // Cleared before the emit, for the same reason as in onCompleted().
    this.paymentInFlight = false;
    this.adyenSuccessfulEvent.emit({
      session_id: session.session_id,
      session_result: sessionResult,
    });
  }

  private stopFastPoll(): void {
    if (this.hostedPollTimeout) {
      clearTimeout(this.hostedPollTimeout);
      this.hostedPollTimeout = undefined;
    }
  }

  private stopHostedWatch(): void {
    if (this.hostedMessageListener) {
      window.removeEventListener('message', this.hostedMessageListener);
      this.hostedMessageListener = undefined;
    }
    if (this.hostedReturnListener) {
      window.removeEventListener('focus', this.hostedReturnListener);
      document.removeEventListener('visibilitychange', this.hostedReturnListener);
      this.hostedReturnListener = undefined;
    }
    this.stopFastPoll();
    if (this.hostedPollTimer) {
      clearInterval(this.hostedPollTimer);
      this.hostedPollTimer = undefined;
    }
    if (this.hostedClosedTimer) {
      clearInterval(this.hostedClosedTimer);
      this.hostedClosedTimer = undefined;
    }
    this.hostedPollRequest?.unsubscribe();
    this.hostedPollRequest = undefined;
    this.hostedCancelCheck?.unsubscribe();
    this.hostedCancelCheck = undefined;
  }

  /** Our own popup, so ours to close: a replaced or abandoned checkout must not stay payable. */
  private closeHostedPopup(): void {
    try {
      if (this.hostedPopup && !this.hostedPopup.closed) {
        this.hostedPopup.close();
      }
    } catch (error) {
      LoggerService.error(error);
    }
    this.hostedPopup = null;
  }

  /**
   * Adyen reports the checkout finished.
   *
   * `Authorised` is the only code that may proceed. `Pending` / `Received` mean
   * an asynchronous method whose money may never arrive — treating those as
   * success creates a confirmed booking against a payment that can still fail.
   */
  private onCompleted(
    session: CreateAdyenSessionResponse,
    result: { resultCode?: string; sessionResult?: string },
    generation: number,
  ): void {
    const lateForCancelled = generation === this.cancelledDropinGeneration;
    if (generation !== this.generation && !lateForCancelled) {
      // A result for a checkout that has since been replaced (the amount changed, a retry). The
      // mounted Drop-in belongs to a NEW session; handing the old one to the host would book
      // against a payment for a different amount. Reversed by the reconciliation job instead.
      LoggerService.error(new Error('Adyen reported a result to a replaced checkout - ignored'));
      return;
    }
    if (this.destroyed) {
      // The customer left this payment method while the SDK was still working. Acting on the
      // result now would emit on a destroyed output into a parent that has moved on. An
      // authorised payment with no booking is reversed by the reconciliation job.
      LoggerService.error(new Error('Adyen reported a result to a destroyed checkout - ignored'));
      return;
    }
    const resultCode = result?.resultCode;
    if (lateForCancelled) {
      // The customer pressed "Cancel payment" while Adyen was authorising, and Adyen answered anyway.
      // Money that was taken (or may still arrive) must not be dropped behind a "cancelled" message:
      // it is settled exactly as if Cancel had not been pressed. A refusal changes nothing - the
      // payment is cancelled either way, and the message already says so.
      if (!CwAdyenComponent.MONEY_RESULT_CODES.includes(resultCode ?? '')) {
        return;
      }
      this.cancelledDropinGeneration = undefined;
      this.errorMessage.set(null);
    }
    this.clearResumeRecord(session.session_id);

    if (resultCode !== ADYEN_RESULT_CODE.AUTHORISED) {
      this.fail(
        resultCode === ADYEN_RESULT_CODE.PENDING || resultCode === ADYEN_RESULT_CODE.RECEIVED
          ? ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING
          : ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.REFUSED
      );
      return;
    }

    if (!result?.sessionResult) {
      // Without it the backend cannot verify the session, and an unverifiable
      // payment must never become a booking.
      this.fail(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
      return;
    }

    // Cleared before the emit: the host hides the payment options in response, which destroys
    // this component, and ngOnDestroy must not then report the payment as never having settled.
    this.paymentInFlight = false;
    this.adyenSuccessfulEvent.emit({
      session_id: session.session_id,
      session_result: result.sessionResult,
    });
  }

  private onFailed(
    session: CreateAdyenSessionResponse,
    result: { resultCode?: string },
    generation: number,
  ): void {
    if (this.destroyed || generation !== this.generation) {
      return;
    }
    this.clearResumeRecord(session.session_id);
    this.fail(
      result?.resultCode === ADYEN_RESULT_CODE.CANCELLED
        ? 'adyen.errors.cancelled'
        : ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.REFUSED
    );
  }

  /**
   * Parks the booking this payment is for, so a page that has to be left mid-payment can finish it
   * on return (AdyenResumeService, read at app level before anything else runs).
   *
   * @returns false when there is nothing to resume with (no host snapshot) or the browser refused
   *          to store it - the caller then must not leave the page
   */
  private writeResumeRecord(kind: AdyenResumeKind, session: CreateAdyenSessionResponse): boolean {
    const partnerId = this.partner()?.id;
    let snapshot: AdyenResumeSnapshot | null = null;
    try {
      snapshot = this.resumeSnapshot()?.() ?? null;
    } catch (error) {
      LoggerService.error(error);
    }
    if (!partnerId || !snapshot) {
      return false;
    }

    const parked = this.adyenResumeService.write({
      v: 1,
      kind,
      partnerId,
      sessionId: session.session_id,
      ...(kind === 'hosted'
        ? { nonce: session.hosted_nonce ?? undefined, token: session.hosted_token ?? undefined }
        : { clientKey: session.client_key, environment: session.environment }),
      holdScope: this.holdScope(),
      items: this.bookingItemCount(),
      snapshot,
      createdAt: Date.now(),
    });
    if (parked) {
      this.parkedSessionId = session.session_id;
    }

    return parked;
  }

  /** Only this session's record: a newer payment of the same partner may already have replaced it. */
  private clearResumeRecord(sessionId: string): void {
    this.adyenResumeService.clearIfOwnedBy(this.partner()?.id, sessionId);
  }

  /** See `forceHosted`. */
  private fallBackToHosted(): void {
    this.forceHosted = true;
    this.resumeUnavailable.set(true);
    this.mountedAmount = null;
    this.teardown();
    this.errorMessage.set(null);
    this.startCheckout();
  }

  private bookingItemCount(): number {
    return (this.isEventCheckout() ? this.eventCart()?.length : this.cart()?.length) ?? 0;
  }

  private teardown(): void {
    // A payment this checkout started is abandoned with it: its result is ignored from here on
    // (see onCompleted), so the host's "already paying" latch would never come off and Book would
    // silently do nothing on the replacement Drop-in.
    if (this.paymentInFlight) {
      this.paymentInFlight = false;
      this.resetAdyenEvent.emit();
    }
    try {
      this.dropin?.unmount?.();
      this.dropin?.remove?.();
    } catch (error) {
      LoggerService.error(error);
    }
    this.dropin = null;
    this.dropinMountedState.set(false);
    if (this.parkedSessionId) {
      // Only this session's record: a settled one is already gone, a newer one is not ours.
      this.clearResumeRecord(this.parkedSessionId);
      this.parkedSessionId = undefined;
    }

    // The hosted checkout's equivalent of unmounting Drop-in: its popup is for this session only.
    this.stopHostedWatch();
    this.closeHostedPopup();
    this.hostedSession.set(null);
    this.hostedAwaiting.set(false);
    this.popupBlocked.set(false);
    this.hostedSettled = false;

    // Cancel whatever is still in flight for the checkout being replaced. Without this,
    // startCheckout() bailed on `mounting` and a price change during the session request
    // was dropped: the Drop-in then mounted on the OLD amount.
    this.generation++;
    this.sessionRequest?.unsubscribe();
    this.sessionRequest = undefined;
    this.mounting = false;
    this.isLoading.set(false);
  }

  /**
   * Pay now, driven by the widget's Book button (Drop-in has no button of its own).
   *
   * Returns false when there is nothing to submit - no mounted Drop-in, or the customer has
   * not completed the card fields - so the caller can leave the shopper on the form instead of
   * reporting a payment failure that never happened. Adyen validates the fields itself and
   * simply does nothing on an incomplete card, which is why this cannot report success.
   * The outcome arrives asynchronously through adyenSuccessfulEvent / resetAdyenEvent.
   */
  submit(): boolean {
    if (this.hostedSession()) {
      // Book is a click too, so the popup may open from it.
      return this.payWithHostedCheckout();
    }
    const dropin = this.dropin as { submit?: () => void; isValid?: boolean } | null;
    if (!dropin || typeof dropin.submit !== 'function') {
      return false;
    }

    try {
      dropin.submit();

      return true;
    } catch (error) {
      // An incomplete card makes Drop-in throw rather than reject; that is the shopper's
      // cue to finish the form, not an error worth replacing the payment box with.
      LoggerService.error(error);

      return false;
    }
  }

  /**
   * A declined or cancelled payment is final for its session, so retrying mints a new one. From
   * the Pending message it is also the customer releasing this booking's hold, explicitly.
   */
  retry(): void {
    this.adyenPaymentHoldService.release(this.partner()?.id, this.holdScope());
    if (this.errorMessage() === ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.ALREADY_BOOKED) {
      // The customer books again on purpose; the made booking's record must not stop them twice.
      this.adyenResumeService.clear(this.partner()?.id);
    }
    this.teardown();
    this.errorMessage.set(null);
    this.startCheckout();
  }

  /**
   * The customer gives up on a payment that is still authorising - a 3DS challenge they cannot
   * finish, a bank app that never answers, a payment window left open. Without this the booking
   * stays locked for as long as the SDK stays silent, which can be for ever.
   *
   * "Cancel" is not proof that nothing was paid. On the hosted page the shopper may have paid a
   * moment ago, with the window's message lost and the next poll not yet run; so the stored result
   * is read once more first, and a payment found there is settled - booked through the one success
   * path - instead of cancelled. For Drop-in, Cancel stays available while Adyen authorises (that is
   * the only time it is shown, and when it is needed), and a result Adyen still delivers for the
   * cancelled attempt is settled the same way (see onCompleted) rather than dropped.
   */
  cancelInFlightPayment(): void {
    if (!this.paymentInFlight || this.hostedCancelCheck) {
      return;
    }
    const session = this.hostedSession();
    if (session) {
      this.checkHostedResultThenCancel(session, this.generation);
      return;
    }
    this.abandonInFlightPayment();
  }

  /**
   * One last read of the hosted result before the payment is abandoned. The timers are stopped so
   * none of them replaces this read; the message listener stays, so a result posted meanwhile still
   * settles the payment (and this read then finds it settled and does nothing).
   */
  private checkHostedResultThenCancel(session: CreateAdyenSessionResponse, generation: number): void {
    this.stopFastPoll();
    if (this.hostedPollTimer) {
      clearInterval(this.hostedPollTimer);
      this.hostedPollTimer = undefined;
    }
    if (this.hostedClosedTimer) {
      clearInterval(this.hostedClosedTimer);
      this.hostedClosedTimer = undefined;
    }
    this.hostedPollRequest?.unsubscribe();
    this.hostedPollRequest = undefined;

    const check = this.paymentService
      .getAdyenHostedResult(session.hosted_nonce as string, session.hosted_token as string)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: AdyenHostedResultResponse) =>
          this.finishHostedCancel(session, generation, this.returnedSessionResult(session, result)),
        error: (error: HttpErrorResponse) => {
          // The read failed, so nothing says the shopper paid: cancel as asked. A payment that did go
          // through without a booking is reversed by the reconciliation job.
          LoggerService.warn(error);
          this.finishHostedCancel(session, generation, null);
        },
      });
    // An answer that came synchronously has already finished the cancel.
    if (!check.closed) {
      this.hostedCancelCheck = check;
    }
  }

  private finishHostedCancel(session: CreateAdyenSessionResponse, generation: number, sessionResult: string | null): void {
    this.hostedCancelCheck = undefined;
    if (this.destroyed || generation !== this.generation || this.hostedSettled) {
      // Settled by a message meanwhile, or the checkout was replaced: nothing is left to cancel.
      return;
    }
    if (sessionResult) {
      this.settleHostedCheckout(session, sessionResult, generation);
      return;
    }
    this.abandonInFlightPayment();
  }

  /**
   * Tearing down bumps the generation, so a hosted result that still arrives for the abandoned
   * attempt is ignored, and it emits resetAdyenEvent, which unlocks the host. The customer then pays
   * again on a fresh session.
   */
  private abandonInFlightPayment(): void {
    // The attempt is abandoned, so nothing may resume it later. A record already sent to /book is a
    // made booking and stays.
    const record = this.adyenResumeService.read(this.partner()?.id);
    if (record && !record.bookingSent) {
      this.adyenResumeService.clear(this.partner()?.id);
    }
    const dropinGeneration = this.dropin ? this.generation : undefined;
    this.mountedAmount = null;
    this.teardown();
    this.errorMessage.set('adyen.errors.cancelled');
    this.cancelledDropinGeneration = dropinGeneration;
  }

  private fail(translationKey: string): void {
    this.paymentInFlight = false;
    if (translationKey === ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING) {
      // Outlives this instance: see AdyenPaymentHoldService.
      this.adyenPaymentHoldService.hold(this.partner()?.id, this.holdScope(), translationKey);
    }
    // The failed session is gone, so there is no mounted amount left to keep in step with the
    // price. Left set, the next price change re-minted behind the error - and after a Pending
    // result that is a second payable session while the first payment may still arrive.
    this.mountedAmount = null;
    this.teardown();
    this.errorMessage.set(translationKey);
    this.resetAdyenEvent.emit(translationKey);
  }

  /**
   * How many payment methods the minted session actually offers.
   *
   * Returns `null` when the SDK does not expose the shape we expect. That distinction is
   * the point: the caller refuses ONLY on a positively-observed zero, so an SDK upgrade
   * that moves this property can never block a customer who could otherwise have paid.
   * Reading it wrong must degrade to today's behaviour, not to "card payment is down".
   */
  private offeredPaymentMethodCount(checkout: unknown): number | null {
    const response = (checkout as { paymentMethodsResponse?: unknown } | null)?.paymentMethodsResponse;
    if (!response || typeof response !== 'object') {
      return null;
    }

    const methods = (response as { paymentMethods?: unknown }).paymentMethods;

    if (Array.isArray(methods)) {
      return methods.length;
    }

    // The shape this guard was written for is an empty OBJECT, not an empty array: the observed
    // DEV answer was `paymentMethods: {}`. An Array.isArray-only test returns null for it - "I
    // could not tell" - and the caller only refuses on 0, so the guard never fired for the one
    // case it exists to catch. Count the keys instead, and keep null for a genuinely unreadable
    // response so an unknown shape still mounts rather than blocking a working checkout.
    if (methods && typeof methods === 'object') {
      return Object.keys(methods).length;
    }

    return null;
  }

  /**
   * A Pending result stands: that money may still arrive, so nothing may mint a second payable
   * session behind it. Only an explicit new selection by the customer starts over.
   */
  private holdsUnsettledPayment(): boolean {
    return this.errorMessage() === ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING;
  }

  /** The booking this checkout pays for, as the hold service scopes a held payment. */
  private holdScope(): string {
    return buildAdyenHoldScope({
      widgetType: this.widgetType(),
      cart: this.cart(),
      eventCart: this.eventCart(),
      storeId: this.storeId(),
      bookingLinkUuid: this.bookingLinkUuid(),
      predefinedSlotUuid: this.predefinedSlotUuid(),
      isCombiServiceSelected: this.isCombiServiceSelected(),
    });
  }

  private isEventCheckout(): boolean {
    return this.widgetType() === WIDGET_CONSTANTS.EVENT || this.widgetType() === WIDGET_CONSTANTS.EVENTS;
  }

  private buildDescription(): string {
    if (this.isEventCheckout()) {
      return this.eventCart()?.map(item => item.currentEvent?.title).filter(Boolean).join(', ') ?? '';
    }

    const names: string[] = [];
    this.cart()?.forEach(cartItem => {
      const services = this.appointmentService.getServicesByIds(
        { serviceIds: cartItem.servicesIds, returnKey: 'services' },
        this.appointmentServicesAvailable()
      );
      services?.forEach((service: { name: string }) => names.push(service.name));
    });

    return names.join(', ');
  }

  /**
   * Adyen expects a full BCP-47 locale; the widget carries short codes.
   * A locale Adyen does not know makes Drop-in fall back to English silently.
   */
  private toAdyenLocale(lang?: string): string {
    switch ((lang ?? '').toLowerCase().slice(0, 2)) {
      case 'de': return 'de-CH';
      case 'fr': return 'fr-CH';
      case 'it': return 'it-CH';
      case 'nl': return 'nl-NL';
      case 'fi': return 'fi-FI';
      case 'pl': return 'pl-PL';
      case 'es': return 'es-ES';
      case 'sv': return 'sv-SE';
      default: return 'en-US';
    }
  }
}
